import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

import PointieTypography from "../components/PointieTypography";
import { trackEvent } from "../../libraries/firebase";

const NON_EN_LANGS = ["fr", "zh", "zh-TW", "pa", "es", "tl", "ar", "ko", "ja"];

export const getFeedHref = (lang) =>
  NON_EN_LANGS.includes(lang) ? `/${lang}/feed.xml` : "/feed.xml";

const RssGlyph = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M6.18 15.64a2.18 2.18 0 1 1 0 4.36 2.18 2.18 0 0 1 0-4.36zM4 4.44A19.56 19.56 0 0 1 19.56 20h-2.83A16.73 16.73 0 0 0 4 7.27V4.44zm0 5.66a13.9 13.9 0 0 1 9.9 9.9h-2.83A11.07 11.07 0 0 0 4 12.93V10.1z" />
  </svg>
);

RssGlyph.propTypes = { size: PropTypes.number };

// Variant `hero`  — pill-shaped, orange-on-dark, used on BlogList + BlogPost hero
// Variant `compact`— icon-only orange round button, used in DefaultFooter socials row
const RssSubscribeButton = ({ variant = "hero", trackingLocation = "blog" }) => {
  const { t, i18n } = useTranslation("nav");
  const lang = i18n.language;
  const feedHref = getFeedHref(lang);
  const label = t("navbar.subscribeRss");

  const isCompact = variant === "compact";

  // Pointie brand yellow (sampled from src/assets/images/pointie_logo.png:
  // dominant pixel rgb(255, 230, 0) = #FFE600). Dark foreground (#344767)
  // mirrors the dark "P" on the yellow logo circle, giving the RSS button
  // the same look & feel as the rest of the Pointie identity.
  const POINTIE_YELLOW = "#FFE600";
  const POINTIE_YELLOW_HOVER = "#E6CF00";
  const DARK_FG = "#344767";

  // Compact: yellow circle that visually echoes the Pointie logo above it,
  // wrapped in the same PointieTypography(variant="h5") pattern as the other
  // social icons next to it. This ensures identical anchor box height (24×32
  // including line-height) and identical vertical alignment within the flex
  // row — matched precisely against Facebook/Instagram/YouTube/Coffee. The
  // inner yellow chip is bumped to 26×26 because circles read visually
  // lighter than the dense filled-square social glyphs at the same dimension.
  if (isCompact) {
    return (
      <Tooltip title={label} arrow>
        <PointieTypography
          component="a"
          href={feedHref}
          target="_blank"
          rel="noreferrer"
          aria-label={label}
          variant="h5"
          onClick={() =>
            trackEvent("blog_subscribe_rss", {
              language: lang,
              location: trackingLocation,
            })
          }
          sx={{
            display: "inline-flex",
            alignItems: "flex-start",
            justifyContent: "center",
            height: "32px",
            width: "24px",
            lineHeight: "27.5px",
            mr: 2.5,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: POINTIE_YELLOW,
              color: DARK_FG,
              transition: "background-color 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = POINTIE_YELLOW_HOVER)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = POINTIE_YELLOW)}
          >
            <RssGlyph size={16} />
          </span>
        </PointieTypography>
      </Tooltip>
    );
  }

  // Hero: yellow chip-style button with dark text, mirroring the Pointie
  // logo's yellow-circle-with-dark-P. High contrast on the dark blog hero
  // gradient (BlogList + BlogPost).
  const styles = {
    color: DARK_FG,
    backgroundColor: POINTIE_YELLOW,
    px: 2,
    py: 1,
    borderRadius: 2,
    gap: 1,
    fontSize: "0.95rem",
    fontWeight: 700,
    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
    "&:hover": {
      backgroundColor: POINTIE_YELLOW_HOVER,
      boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
    },
  };

  return (
    <Tooltip title={label} arrow>
      <IconButton
        component="a"
        href={feedHref}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
        onClick={() =>
          trackEvent("blog_subscribe_rss", {
            language: lang,
            location: trackingLocation,
          })
        }
        sx={styles}
      >
        <RssGlyph size={20} />
        <span style={{ marginLeft: 4 }}>{label}</span>
      </IconButton>
    </Tooltip>
  );
};

RssSubscribeButton.propTypes = {
  variant: PropTypes.oneOf(["hero", "compact"]),
  trackingLocation: PropTypes.string,
};

export default RssSubscribeButton;
